<template>
  <div>
    <ProgressBar active-step="Tell us about yourself" />
    <PageHeading
      :default-heading="pageHeader"
      :edit-heading="pageHeader"
    />
    <form
      novalidate="novalidate"
      @submit.prevent
    >
      <div class="pvd-container container-divider">
        <h2 class="pvd-heading-L">
          {{ pd_content.personal_information_section_header }}
        </h2>
        <apx-full-name
          id="fullName"
          :group-label="pd_content.name_label"
          :first-name="pu_firstName"
          :first-name-label="pd_content.first_name_label"
          :middle-name="pu_middleName"
          :middle-name-label="pd_content.middle_name_label"
          :last-name="pu_lastName"
          :last-name-label="pd_content.last_name_label"
          required
        />
        <apx-ssn
          id="ssn"
          :value="showSecurityNumber"
          :label="pd_content.ssn_label"
          mask-regex="\d\d\d-\d\d-"
          :mask-pattern="maskSsn"
          required
        />
        <apx-date-select
          id="dateOfBirth"
          :group-label="pd_content.dob_label"
          :month-value="month"
          :month-label="pd_content.dob_month_label"
          :day-value="date"
          :day-label="pd_content.dob_day_label"
          :year-value="year"
          :year-label="pd_content.dob_year_label"
          :validation-text="pd_content.dob_invalid_date"
          default-select-label="Select"
          :min-date="pd_content.min_date_allowed_for_dob"
          eighteen-or-above
          required
          :date-input-value="pu_dob"
        />
        <apx-select
          id="residenceCountryList"
          :value="pu_residenceCountry"
          :label="pd_content.citizenship_country_label"
          :error-text="pd_content.citizenship_country_error"
          required
        />
        <apx-phone
          v-if="showPhone"
          id="phoneNumber"
          :telephone-number="pu_dayPhone"
          :telephone-number-label="pd_content.phone_number_label.value"
          required
        />
        <apx-email
          id="email"
          :readonly="readonlyEmail ? true : false"
          :supplemental-text="readonlyEmail ? pd_content.email_supplemental : false"
          :value="pu_email"
          :label="pd_content.email_label"
          required
        />
        <apx-text-input
          v-if="promotionalRegtypes"
          id="promotionalInp"
          :label="pd_content.offer_codes.labelText"
        />
      </div>
      <div class="pvd-container container-divider">
        <h2 class="pvd-heading-L">
          {{ pd_content.address_information_section_header }}
        </h2>
        <p class="m-small-top">
          {{ pd_content.address_information_section_sub_header }}
        </p>
        <apx-address
          id="address"
          :address-line1="pu_leg_line1"
          :address-line2="pu_leg_line2"
          :city-name="pu_leg_city"
          :state-code="pu_leg_state"
          :zip-code="pu_leg_zip"
          :address-labels="JSON.stringify(addressLabels)"
          prevent-pobox
          required
        />
      </div>
      <NavigationComponent
        @next-action="handleWidgetSubmit"
      />
      <DisclosureComponent
        v-if="routeId === 'trust'"
        :paragraph-disclosure="pd_content.trust_page_disclosure_paragraph"
      />
      <DisclosureComponent
        v-else
        :additional-disclosure="pd_content.page_disclosure_additional"
        :paragraph-disclosure="pd_content.page_disclosure_paragraph"
      />
    </form>
    <!-- 2FA widget place holder, when we load widget script it will the
      load the widget in this placeholder -->
    <div
      id="fs-steps"
      class="personal-info-page"
      data-operation="IdentifyUser"
      data-step="Init"
      style="display: none"
    >
      &nbsp;
    </div>
  </div>
</template>

<script>
import DisclosureComponent from '../components/DisclosureComponent.vue';
import ProgressBar from '../components/ProgressBar.vue';
import NavigationComponent from '../components/NavigationComponent.vue';
import PageHeading from '../components/PageHeading.vue';
import addressAutocomplete from '../mixins/addressAutocomplete';
import dataHelper from '../mixins/dataHelper';
import dataSanitize from '../mixins/dataSanitize';
import measurement from '../mixins/measurement';
import utilityFunctions from '../mixins/utility';
import validationHelper from '../mixins/validationHelper';
import widgetHelper from '../mixins/widgetHelper';

export default {
  name: 'PersonalInformation',
  components: {
    DisclosureComponent,
    ProgressBar,
    NavigationComponent,
    PageHeading,
  },
  mixins: [addressAutocomplete, dataHelper, dataSanitize, measurement,
    utilityFunctions, validationHelper, widgetHelper],
  data() {
    return {
      backButtonAttempt: false,
    };
  },
  computed: {
    month() {
      if (this.pu_dob) {
        const splitDob = this.pu_dob.split('/');
        return splitDob[0];
      }
      return '';
    },
    date() {
      if (this.pu_dob) {
        const splitDob = this.pu_dob.split('/');
        return splitDob[1];
      }
      return '';
    },
    year() {
      if (this.pu_dob) {
        const splitDob = this.pu_dob.split('/');
        return splitDob[2];
      }
      return '';
    },
    addressLabels() {
      return {
        addressLine1: this.pd_content.address_line1_label,
        addressLine2: this.pd_content.address_line2_label,
        cityName: this.pd_content.city_label,
        stateCode: this.pd_content.state_label,
        zipCode: this.pd_content.zipcode_label,
      };
    },
    readonlyEmail() {
      if (this.uc_isCustomer) {
        return !(!this.pu_email || this.$globalData.personalInformation.emailEdited || this.checkEmailPrefillError());
      }
      return false;
    },
    showPhone() {
      return !this.uc_isCustomer || !this.pu_dayPhone || this.$globalData.personalInformation.dayPhoneEdited
        || this.checkPhonePrefillError();
    },
    maskSsn() {
      return this.uc_isCustomer ? '*****' : '';
    },
    showSecurityNumber() {
      return this.getSecurityNumber(this.pu_gNumber, this.pu_ssn);
    },
    pageHeader() {
      const pageHeader = this.pd_content.page_header;
      if (pageHeader) {
        const heading = this.cs_editMode ? pageHeader.edit : pageHeader.default;

        if (heading) {
          document.title = this.setTitle(heading);
        }
        return heading;
      }
      return '';
    },
    promotionalRegtypes() {
      return this.isPromotionalValidForRegtype(this.$dpTogglesObj.AO_KAPAO_PROMOTIONAL_CODE_TOGGLE);
    },
  },
  created() {
    if (this.cs_editMode) {
      this.unsubscribe = this.sanitizeInstanceData('primaryUser');
    }
  },
  beforeDestroy() {
    if (this.unsubscribe) {
      this.unsubscribe();
    }
  },
  mounted() {
    const backButtonAction = this.uc_isCustomer ? () => this.showBackWarning(this.pd_content.back_button_disabled_warning) : () => this.$emit('invoke-previous');
    this.configureBackButtonFunctionality(() => { backButtonAction(); });

    this.trackEdit(this.cs_editMode, 'edit-personal-information');
    this.removeModalClassIfExists();
    const changeEventName = 'value-changed';
    this.populateCountryList('#residenceCountryList');
    this.populateStateList('#address');

    const fullNameComponent = document.querySelector('#fullName');
    fullNameComponent.addEventListener(changeEventName, this.fullNameChanged);
    fullNameComponent.validationRules = this.buildRules('fullName');
    fullNameComponent.readonly = this.uc_isCustomer && ((this.pu_firstName && this.pu_lastName) || this.cs_editMode)
      && !this.checkFullNamePrefillError();

    const ssnComponent = document.querySelector('#ssn');
    ssnComponent.addEventListener(changeEventName, this.ssnChanged);
    ssnComponent.validationRules = this.buildRules('ssn');
    ssnComponent.readonly = this.uc_isCustomer && (this.pu_ssn || this.cs_editMode);

    const dobComponent = document.querySelector('#dateOfBirth');
    dobComponent.addEventListener(changeEventName, this.dateOfBirthChanged);
    dobComponent.validationRules.date = this.pd_validationRules.customerIdentity.birthDate;
    dobComponent.readonly = this.uc_isCustomer && (this.pu_dob || this.cs_editMode);

    if (this.showPhone) {
      const phoneComponent = document.querySelector('#phoneNumber');
      phoneComponent.addEventListener(changeEventName, this.phoneNumberChanged);
      phoneComponent.validationRules = this.buildRules('phone');
    }
    if (!this.readonlyEmail) {
      const emailComponent = document.querySelector('#email');
      emailComponent.addEventListener(changeEventName, this.emailChanged);
      emailComponent.validationRules = this.buildRules('email');
    }
    const addressComponent = document.querySelector('#address');
    addressComponent.addEventListener(changeEventName, this.addressChanged);
    addressComponent.validationRules = this.buildRules('address');

    document.querySelector('#residenceCountryList')
      .addEventListener(changeEventName, this.residenceCountryChanged);

    this.commitPrimaryUserData('Yes', 'receiveAccountDocElectronically');

    if (this.shouldCall2FAWidget(this.cs_stateName,
      this.uc_isCustomer, this.uc_isRep, this.routeId)
      && (this.cs_navAction === 'next' || this.cs_navAction === 'previous')) {
      this.loadExternalScript(this.pd_envConfig.twoFAWidgetURL);
    }
    this.initGoogleAutocomplete('address-addressLine1', this.fillAddressValues);

    if (this.isPromotionalValidForRegtype(this.$dpTogglesObj.AO_KAPAO_PROMOTIONAL_CODE_TOGGLE)) {
      this.setPromotionalValidations();
    }
  },

  methods: {
    fillAddressValues(address) {
      const event = {
        detail: {
          address,
        },
      };

      const line1Element = document.getElementById('address-addressLine1');
      line1Element.value = event.detail.address.addressLine1;
      line1Element.dispatchEvent(new Event('change'));

      document.getElementById('address-addressLine2').value = event.detail.address.addressLine2;

      const cityNameElement = document.getElementById('address-cityName');
      cityNameElement.value = event.detail.address.cityName;
      cityNameElement.dispatchEvent(new Event('change'));

      const stateCodeElement = document.getElementById('address-stateCode');
      stateCodeElement.value = event.detail.address.stateCode;
      stateCodeElement.dispatchEvent(new Event('change'));

      const zipCodeElement = document.getElementById('address-zipCode');
      zipCodeElement.value = event.detail.address.zipCode;
      zipCodeElement.dispatchEvent(new Event('change'));

      this.addressChanged(event);
    },
    ssnChanged(event) {
      this.commitPrimaryUserData(event.detail.value, 'ssn');
    },
    fullNameChanged(event) {
      this.commitPrimaryUserData(event.detail.fullname.firstName, 'firstName');
      this.commitPrimaryUserData(event.detail.fullname.middleName, 'middleName');
      this.commitPrimaryUserData(event.detail.fullname.lastName, 'lastName');
    },
    dateOfBirthChanged(event) {
      this.commitPrimaryUserData(`${event.detail.month}/${event.detail.day}/${event.detail.year}`, 'dob');
    },
    phoneNumberChanged(event) {
      this.commitPrimaryUserData(event.detail.telephoneNumber, 'dayPhone');
      this.$globalData.personalInformation.dayPhoneEdited = true;
      this.commitPrimaryUserData(event.detail.checked, 'mobilePhoneCheckIndicator');
    },
    emailChanged(event) {
      this.commitPrimaryUserData(event.detail.value, 'email');
      this.$globalData.personalInformation.emailEdited = true;
    },
    residenceCountryChanged(event) {
      this.commitPrimaryUserData(event.detail.selectValue, 'residenceCountry');
    },
    addressChanged(event) {
      this.commitPrimaryUserData(event.detail.address.addressLine1, 'addresses.legal.line1');
      this.commitPrimaryUserData(event.detail.address.addressLine2, 'addresses.legal.line2');
      this.commitPrimaryUserData(event.detail.address.cityName, 'addresses.legal.city');
      this.commitPrimaryUserData(event.detail.address.stateCode, 'addresses.legal.state');
      this.commitPrimaryUserData(event.detail.address.zipCode, 'addresses.legal.zip');
    },
    checkFullNamePrefillError() {
      return !!(this.safeObjectGet(this.pu_formErrors, 'personalInformation.firstName.error')
          || this.safeObjectGet(this.pu_formErrors, 'personalInformation.middleName.error')
          || this.safeObjectGet(this.pu_formErrors, 'personalInformation.lastName.error'));
    },
    checkPhonePrefillError() {
      return this.safeObjectGet(this.pu_formErrors, 'personalInformation.telephoneNumber.error');
    },
    checkEmailPrefillError() {
      return this.safeObjectGet(this.pu_formErrors, 'personalInformation.email.error');
    },

    setPromotionalValidations() {
      /* eslint-disable camelcase */
      const { offer_codes } = this.pd_content;
      const that = this;
      const defaultRegexStr = '(.*?)';
      const promotionalInp = document.querySelector('#promotionalInp');
      if (promotionalInp && offer_codes && offer_codes.json) {
      /* eslint-enable camelcase */
        promotionalInp.validationRules = {
          validations: {
            required: false,
            max: 50,
            min: 0,
            regex: defaultRegexStr,
          },
        };
        promotionalInp.validationText = {
          validations: {
            max: offer_codes.maxErrorMsg,
            regex: offer_codes.invalidCodeErrorMsg,
          },
        };
        promotionalInp.addEventListener('value-changed', (event) => {
          const changedValue = event.detail.value;
          if (changedValue !== '') {
            promotionalInp.validationRules.validations.regex = (that.checkOfferCodeExist(offer_codes.json, changedValue) === true) ? defaultRegexStr : '$a';
          } else {
            promotionalInp.validationRules.validations.regex = defaultRegexStr;
          }
          that.commitPrimaryUserData(changedValue, 'promotionalText');
          that.commitPrimaryUserData(that.getPromotionalCode(offer_codes.json, changedValue), 'promotionalCode');
        });
      }
    },

    checkOfferCodeExist(offerCodeObj, offerCode) {
      const keysArr = Object.keys(offerCodeObj);
      return keysArr.find((key) => key.toUpperCase() === offerCode.toUpperCase()) !== undefined;
    },

    getPromotionalCode(offerCodeObj, promotionalText) {
      const selectedKey = Object.keys(offerCodeObj).find((key) => key.toUpperCase() === promotionalText.toUpperCase());
      return selectedKey ? offerCodeObj[selectedKey] : '';
    },
  },
};

</script>

<style scoped>
  .top-margin-zero {
    margin-top: 0;
  }
</style>
